import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { VIDEO_UPLOAD_LOADING } from "../../../../constants/loading.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SOCIAL_POST_TYPES } from "../../../../cores/socialPost/consts/socialPostType.constant";
import { inferComposerHasVideo } from "../../../../cores/socialPost/helpers/tiktokComposerVideo";
import { LoadingSpinnerWembii } from "../../LoadingSpinnerWembii";
import { VideoPlayer } from "./VideoPlayer";
import "./MediaPreview.scss";

interface Props {
  className?: string;
}

export const MediaPreview = ({ className = "" }: Props) => {
  const { images, video, selectedPost } = useSocialPostCore();
  const { isLoading } = useGlobalCore();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [videoType, setVideoType] = useState("");

  const showVideoPlayer = useMemo(
    () => inferComposerHasVideo(video, selectedPost),
    [video, selectedPost]
  );

  const imageItems = useMemo(() => {
    if (images.length > 0) {
      return images.map((image) => ({
        key: image.id || image.name,
        src: image.data,
      }));
    }

    if (
      selectedPost?.post.type === SOCIAL_POST_TYPES.IMAGE &&
      selectedPost.media
    ) {
      const postMedia = Array.isArray(selectedPost.media)
        ? selectedPost.media
        : [selectedPost.media];
      if (postMedia.length > 0) {
        return postMedia.map((media) => ({
          key: media.key || media.url,
          src: media.url,
        }));
      }
    }

    return [];
  }, [images, selectedPost]);

  const composerImageOrderKey = useMemo(
    () => (images.length > 0 ? images.map((i) => i.name).join("|") : ""),
    [images]
  );

  useEffect(() => {
    setActiveImageIndex(0);
  }, [imageItems.length, composerImageOrderKey]);

  useEffect(() => {
    if (video) {
      setVideoType(video.type);
    }
  }, [video]);

  const goToPreviousImage = () => {
    setActiveImageIndex((current) =>
      current === 0 ? imageItems.length - 1 : current - 1
    );
  };

  const goToNextImage = () => {
    setActiveImageIndex((current) =>
      current === imageItems.length - 1 ? 0 : current + 1
    );
  };

  const hasImageCarousel = imageItems.length > 1;

  return (
    <div className="media-preview">
      {imageItems.length > 0 && (
        <div className="media-preview-carousel">
          <img
            className={className}
            src={imageItems[activeImageIndex]?.src}
            alt="preview"
          />

          {hasImageCarousel && (
            <>
              <button
                type="button"
                className="media-preview-carousel-button media-preview-carousel-button--previous"
                onClick={goToPreviousImage}
                aria-label="Imagen anterior"
              >
                <FontAwesomeIcon icon={["fas", "chevron-left"]} />
              </button>
              <button
                type="button"
                className="media-preview-carousel-button media-preview-carousel-button--next"
                onClick={goToNextImage}
                aria-label="Imagen siguiente"
              >
                <FontAwesomeIcon icon={["fas", "chevron-right"]} />
              </button>
              <div className="media-preview-carousel-counter">
                {activeImageIndex + 1} / {imageItems.length}
              </div>
              <div className="media-preview-carousel-dots">
                {imageItems.map((image, index) => (
                  <button
                    key={image.key}
                    type="button"
                    className={`media-preview-carousel-dot ${
                      index === activeImageIndex ? "active" : ""
                    }`}
                    onClick={() => setActiveImageIndex(index)}
                    aria-label={`Ver imagen ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      <LoadingSpinnerWembii active={isLoading(VIDEO_UPLOAD_LOADING)}>
        {(showVideoPlayer) && (
          <VideoPlayer
            src={video ? video!.data : selectedPost?.media[0]?.url || ''}
            type={videoType}
            className={className}
            autoPlay={false}
            muted={true}
            loop={true}
          />
        )}
      </LoadingSpinnerWembii>
    </div>
  );
};
